import { AppointmentPhase, Status } from "components";
import { type FilterValues } from "components/filters/FilterContext";
import React from "react";
import ShowDateTime from "show/components/ShowDateTime";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import ArtistFilter from "components/filters/Filters/ArtistFilter";
import ProductionFilter from "components/filters/Filters/ProductionFilter";
import StatusFilter from "components/filters/Filters/StatusFilter";
import PeriodFilter from "components/filters/Filters/PeriodFilter";
import BookkeepingFilter from "components/filters/Filters/BookkeepingFilter";
import { mapArtistFilterToInstruction, mapBookkeepingFilterToInstruction, mapKeywordFilterToInstruction, mapPeriodFilterToInstruction, mapProductionFilterToInstruction, mapResponseToArtistFilter, mapResponseToBookkeepingFilter, mapResponseToKeywordFilter, mapResponseToPeriodFilter, mapResponseToProductionFilter, mapResponseToStatusFilter, mapStatusFilterToInstruction } from "components/filters/mappingUtils";
import SelectableList from "components/ListTemplate/SelectableList";

interface Props {
  showId: number
  onTimetableCopied: () => void
  defaultFilter: server.dto.ListShowInternalFilters
  initialInstruction: server.dto.ListShowInternalInstruction
}

const LoadFromShow = (props: Props) => {
  const mapResponseToFilterValues = (
    response: server.dto.ListShowInternalFilters,
    values: FilterValues
  ): Partial<FilterValues> => ({
    keyword: mapResponseToKeywordFilter(response.keyword),
    artist: mapResponseToArtistFilter(response.artists),
    bookkeepings: mapResponseToBookkeepingFilter(response.bookkeepings),
    statuses: mapResponseToStatusFilter(response.statuses, values.statuses),
    period: mapResponseToPeriodFilter(response.period),
    production: mapResponseToProductionFilter(response.productions)
  });

  const mapFilterValuesToInstruction = (
    filter: FilterValues,
    page: number,
    perPage: number
  ): server.dto.ListShowInternalInstruction => {
    return {
      keyword: mapKeywordFilterToInstruction(filter.keyword),
      artistIds: mapArtistFilterToInstruction(filter.artist),
      bookkeepingIds: mapBookkeepingFilterToInstruction(filter.bookkeepings),
      statuses: mapStatusFilterToInstruction(filter.statuses),
      period: mapPeriodFilterToInstruction(filter.period),
      productionIds: mapProductionFilterToInstruction(filter.production),
      excludeShowIds: [props.showId],
      page,
      perPage,
      onlyMySelection: false,
      hasTimetable: true
    } satisfies server.dto.ListShowInternalInstruction;
  };

  const onSelect = async (show: server.dto.ShowForListInternal) => {
    await window.CoreApi.Timetable.Duplicate({
      sourceShowId: show.showId,
      targetShowId: props.showId
    });
    props.onTimetableCopied();
  };

  return <div className="flex grow1" style={{ minHeight: 0 }}>
    <SelectableList
      perPage={20}
      defaultFilter={props.defaultFilter}
      initialInstruction={props.initialInstruction}
      excludeKeys={[props.showId]}
      excludeKeysName="excludeShowIds"
      getId={show => show.showId}
      onSelect={onSelect}
      layout={{
        type: "card",
        sections: {
          renderTitle: () => null,
          hideTitle: () => true,
          renderBody: show => <div className="flex">
            <div className="margin-right-small"><AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} /></div>
            <div className="grow1">
              <div>{show.title}</div>
              <Status status={show.status} small />
              <div>
                <ShowDateTime dateTime={show.dateTime} />
              </div>
            </div>
          </div>,
          renderFooter: () => null,
          hideFooter: () => true
        }
      }}
      filters={<>
        <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
        <ArtistFilter />
        <ProductionFilter />
        <StatusFilter initialStatuses={site.currentEnvironment.statuses.show} />
        <PeriodFilter />
        <BookkeepingFilter />
      </>}
      fetchFunction={window.CoreApi.Show.PaginatedInternalEnvironment}
      mapFilterValuesToInstruction={mapFilterValuesToInstruction}
      mapResponseToFilterValues={mapResponseToFilterValues}
    />
  </div>;
};

export default LoadFromShow;
